// Common API configuration and utilities
import { customAlphabet } from 'nanoid';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '@/config/eveBackend';

const SUPABASE_KEY = SUPABASE_ANON_KEY;
const SUPABASE_API_KEY = SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_KEY) {
  console.log('SUPABASE_URL and SUPABASE_KEY must be set');
}
else {
  console.log('SUPABASE_URL and SUPABASE_KEY are set');
}

export const API_BASE_URL = `${SUPABASE_URL}/functions/v1`;
export const API_KEY = SUPABASE_API_KEY || SUPABASE_KEY;

export const generateAlphanumericId = customAlphabet('abcdefghijklmnopqrstuvwxyz0123456789', 10);

/**
 * Anon-key-only headers. Safe for ungated edge functions reachable from the
 * public onboarding flow, but NOT for any function behind `requireAuth`
 * (`supabase/functions/_shared/auth.ts`) — that gate accepts only the
 * service-role key or a token resolving to a real user, so the anon key gets a
 * 401. Use `getAuthHeaders()` for anything authenticated.
 */
export const headers = {
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${API_KEY}`
};

/**
 * Headers for direct `fetch` calls to edge functions, carrying the live session
 * access token when one exists. Falls back to the anon key when there is no
 * session, so the anonymous onboarding flow keeps behaving exactly as it does
 * with `headers` above.
 *
 * The Supabase client is imported dynamically to keep this widely-imported
 * module free of a static dependency on the client singleton, matching
 * `resellerSession.ts` and `deviceMfaCookie.ts`.
 */
let supabaseClientModule: Promise<typeof import('@/integrations/supabase/client')> | null = null;

const loadSupabaseClient = () => {
  // Memoised so concurrent callers share a single module resolution.
  supabaseClientModule ??= import('@/integrations/supabase/client');
  return supabaseClientModule;
};

export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  let accessToken: string | undefined;

  try {
    const { supabase } = await loadSupabaseClient();
    const { data } = await supabase.auth.getSession();
    accessToken = data?.session?.access_token;
  } catch (error) {
    // Never throw: callers are UI fetch paths whose error handling assumes a
    // network/HTTP failure, not a header-construction failure. Degrading to the
    // anon key preserves today's behaviour for anonymous surfaces.
    console.warn('[apiConfig] Could not read the Supabase session; falling back to the anon key.', error);
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken || API_KEY}`,
    'apikey': API_KEY
  };
};

export const STEP_STORAGE_KEY = 'figabl_current_step';
